import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Shield, MapPin, Users, Calendar, Clock, DollarSign,
    Plus, Trash2, CheckCircle, AlertCircle, Play, Save, ArrowLeft,
    Building2, Search, ChevronDown, User as UserIcon, Receipt, UserPlus, Ban,
    X, Pencil, Loader2, FileText, Crosshair, ShieldOff, SlidersHorizontal, History,
} from 'lucide-react';
import GeofenceMapPicker, { GeofenceValue } from '../components/GeofenceMapPicker';
import { ServizioFiduciarioService, ClientService, UserService } from '../services/apiService';
import { useToast } from '../components/ToastContext';
import { TabContabilitaServizio } from '../components/TabContabilitaServizio';
import { TabReportServizio } from '../components/TabReportServizio';
import ClienteCreateModal from '../components/ClienteCreateModal';
import LavoroAPosterioriModal from '../components/LavoroAPosterioriModal';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';
import { clientiDellaSede } from '../utils/clientDisplay';
import { getClientDisplayName, getClientRagioneSociale, getClientReferente, isClienteAzienda } from '../utils/clientDisplay';
import NumberInput from '../components/NumberInput';
import { ConflittiDisponibilitaModal, ConflittoDisponibilita } from '../components/ConflittiDisponibilitaModal';
import { useAvvisoCompresenze } from '../components/AvvisoCompresenze';
import { ConflittiOrario, estraiConflitti, EsitoConflitti } from '../components/ConflittiOrario';
import { getStatoOperativoTurno } from '../utils/incaricoUtils';
import { generaDateTurni } from '../utils/turniDates';

export const GestioneServizioView: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const { user, currentSedeId, sedi } = useAuth();
    const isNew = id === 'nuovo';
    // L'ADMIN sceglie sempre la sede del servizio, in creazione e in modifica,
    // così è chiaro a quale sede appartiene. Per il RESPONSABILE coincide con la
    // sua e il picker non compare.
    const showSedePicker = !!user && user.role === UserRole.ADMIN;

    const [isLoading, setIsLoading] = useState(false);
    const [clientiDb, setClientiDb] = useState<any[]>([]);
    const [utentiDb, setUtentiDb] = useState<any[]>([]);

    const [servizio, setServizio] = useState<any>({
        id: null as number | null,
        nome: '',
        indirizzo: '',
        clientId: '',
        tariffaOrariaCliente: 0,
        // Tariffazione cliente: 'ORARIA' (default) o 'FORFAIT' (prezzo unico per il
        // contratto). Con FORFAIT ricavo cliente e contabilità valgono prezzoForfaitCliente.
        tipoTariffaCliente: 'ORARIA' as 'ORARIA' | 'FORFAIT',
        prezzoForfaitCliente: 0,
        stato: 'Attivo',
        sedeId: '' as string | number,
        datiAggiuntiviJson: null as string | null,
        // Geofence del servizio: i 4 campi viaggiano insieme. Con attivo=false il BE
        // azzera gli altri.
        geofenceAttivo: false,
        geofenceLat: null as number | null,
        geofenceLng: null as number | null,
        geofenceRaggioMt: 100 as number | null,
    });

    const [tariffe, setTariffe] = useState<any[]>([]);
    const [turni, setTurni] = useState<any[]>([]);
    // Turno già svolto da inserire con report approvato (es. fermo del gestionale).
    const [showAPosteriori, setShowAPosteriori] = useState(false);

    const [newUser, setNewUser] = useState('');
    const [newRate, setNewRate] = useState('');

    // Modifica inline della tariffa €/h di un collaboratore.
    const [editingTariffaId, setEditingTariffaId] = useState<number | null>(null);
    const [editTariffaValue, setEditTariffaValue] = useState<string>('');
    const [savingTariffa, setSavingTariffa] = useState(false);

    // Scadenza di accettazione opzionale per singolo turno, impostata da un mini
    // modale. Alla scadenza il job autoRifiutaTurniScaduti rifiuta il turno.
    const [scadenzaTurno, setScadenzaTurno] = useState<any | null>(null);
    const [scadenzaValue, setScadenzaValue] = useState<string>('');
    const [scadenzaSaving, setScadenzaSaving] = useState(false);
    // Modale modifica ora di fine turno.
    const [editFineTurno, setEditFineTurno] = useState<any | null>(null);
    const [editFineValue, setEditFineValue] = useState<string>('');
    const [editFineSaving, setEditFineSaving] = useState(false);
    const [editFineError, setEditFineError] = useState<string | null>(null);
    // Turno non avviato: si spostano anche giorno e inizio.
    const [editDataTurno, setEditDataTurno] = useState<string>('');
    const [editInizioValue, setEditInizioValue] = useState<string>('');
    const [editConflitti, setEditConflitti] = useState<EsitoConflitti | null>(null);
    // Avviso se sul servizio c'è già un altro collaboratore negli stessi orari (non blocca).
    const avvisoCompresenze = useAvvisoCompresenze();
    const [editConferma, setEditConferma] = useState(false);
    // Modale modifica finestra di tolleranza su un turno esistente.
    const [editRangeTurno, setEditRangeTurno] = useState<any | null>(null);
    const [editRange, setEditRange] = useState({ rangeInizioDa: '', rangeInizioA: '', rangeFineDa: '', rangeFineA: '' });
    const [editRangeSaving, setEditRangeSaving] = useState(false);
    const [editRangeError, setEditRangeError] = useState<string | null>(null);

    // Conflitti di disponibilità dopo il salvataggio massivo: il backend salta i
    // turni in cui il collaboratore è già impegnato e li restituisce in `conflicts`.
    const [conflittiTurni, setConflittiTurni] = useState<{
        list: ConflittoDisponibilita[]; collab?: string; creati: number; totale: number;
    } | null>(null);
    // Payload dell'ultimo salvataggio massivo, per reinviare con forceOverride=true
    // le righe che il titolare seleziona nella modale conflitti (collaboratore che
    // ha già fatto stop, quindi sovrascrivibile).
    const [pendingTurniPayload, setPendingTurniPayload] = useState<any[] | null>(null);

    const [rule, setRule] = useState({
        userId: '',
        dataDal: '',
        dataAl: '',
        giorniSettimana: [] as number[],
        oraInizio: '08:00',
        oraFine: '14:00',
        notturno: false,
        // Finestra di tolleranza opzionale su inizio/fine turno: 4 orari "HH:mm"
        // (vuoti = disattivata). Si inviano come LocalTime, null se vuoti.
        rangeInizioDa: '',
        rangeInizioA: '',
        rangeFineDa: '',
        rangeFineA: '',
        // Scadenza di accettazione applicata a tutti i turni generati, formato
        // datetime-local. Vuota = nessuna scadenza: il turno resta ASSEGNATO finché
        // il collaboratore non risponde.
        dataScadenzaAccettazione: '',
    });

    // Selettore cliente
    const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Creazione cliente inline, stessa UX di NuovoClienteView (vedi ClienteCreateModal).
    const [isCreateClientOpen, setIsCreateClientOpen] = useState(false);

    // Tab attivo
    const [activeTab, setActiveTab] = useState<'pianificazione' | 'report' | 'contabilita'>('pianificazione');

    const GIORNI = [
        { id: 1, label: 'Lun' }, { id: 2, label: 'Mar' }, { id: 3, label: 'Mer' },
        { id: 4, label: 'Gio' }, { id: 5, label: 'Ven' }, { id: 6, label: 'Sab' }, { id: 0, label: 'Dom' }
    ];

    // In creazione precarica la sede corrente o, per il RESPONSABILE, la sua.
    // Con ADMIN e "Tutte le sedi" serve il picker.
    useEffect(() => {
        if (!isNew || !user) return;
        if (typeof currentSedeId === 'number') {
            setServizio((prev: any) => ({ ...prev, sedeId: currentSedeId }));
        } else if (user.role !== UserRole.ADMIN && user.sedeId) {
            setServizio((prev: any) => ({ ...prev, sedeId: user.sedeId! }));
        }
    }, [isNew, user, currentSedeId]);

    useEffect(() => {
        caricaDatiBase();
        if (!isNew) caricaServizio();

        // Chiusura del dropdown cliccando fuori
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsClientDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [id]);

    const caricaDatiBase = async () => {
        try {
            const [clientiRes, utentiRes] = await Promise.all([
                ClientService.getAll(),
                UserService.getAll()
            ]);
            setClientiDb(clientiRes.data || clientiRes);
            setUtentiDb(utentiRes.data || utentiRes);
        } catch (e) {
            addToast("Errore caricamento anagrafiche", "error");
        }
    };

    const caricaServizio = async () => {
        setIsLoading(true);
        try {
            const res = await ServizioFiduciarioService.getById(Number(id));
            const data = res.data || res;
            setServizio({
                id: data.id,
                nome: data.nome,
                indirizzo: data.indirizzo,
                clientId: data.clientId ? String(data.clientId) : '',
                tariffaOrariaCliente: data.tariffaOrariaCliente,
                tipoTariffaCliente: (data.tipoTariffaCliente === 'FORFAIT' ? 'FORFAIT' : 'ORARIA'),
                prezzoForfaitCliente: data.prezzoForfaitCliente != null ? Number(data.prezzoForfaitCliente) : 0,
                stato: data.stato || 'Attivo',
                sedeId: data.sedeId || '',
                // Dati extra (movimenti contabili) per la sezione Pagamenti di TabContabilitaServizio.
                datiAggiuntiviJson: data.datiAggiuntiviJson || null,
                // Geofence configurato sul servizio.
                geofenceAttivo: !!data.geofenceAttivo,
                geofenceLat: data.geofenceLat != null ? Number(data.geofenceLat) : null,
                geofenceLng: data.geofenceLng != null ? Number(data.geofenceLng) : null,
                geofenceRaggioMt: data.geofenceRaggioMt != null ? Number(data.geofenceRaggioMt) : 100,
            } as any);
            setTariffe(data.tariffeCollaboratori || []);
            setTurni(data.turni || []);
        } catch (e: any) {
            // 404/403: utente senza permessi sul servizio. Il messaggio lo mostra già
            // l'interceptor globale. Il responsabile, su un servizio di una sede che
            // non gestisce, va ai propri turni; gli altri tornano alla dashboard.
            const status = e?.response?.status;
            if (status === 404 || status === 403) {
                const target = status === 403 && user?.role === UserRole.RESPONSABILE ? '/incarichi' : '/dashboard';
                navigate(target, { replace: true });
                return;
            }
            addToast("Errore caricamento servizio", "error");
        } finally {
            setIsLoading(false);
        }
    };

    // Salvataggio servizio
    // Salvataggio e generazione in corso: bloccano il doppio click (servizi o turni doppi).
    const [savingBase, setSavingBase] = useState(false);
    const [generandoTurni, setGenerandoTurni] = useState(false);
    const onSaveServizio = async () => {
        if (savingBase) return;
        setSavingBase(true);
        try { await handleSaveServizio(); } finally { setSavingBase(false); }
    };
    const onGeneraTurni = async () => {
        if (generandoTurni) return;
        setGenerandoTurni(true);
        try { await generaTurni(); } finally { setGenerandoTurni(false); }
    };

    const handleSaveServizio = async () => {
        if (!servizio.nome || !servizio.clientId) {
            addToast("Nome e Cliente sono obbligatori", "error");
            return;
        }
        // L'ADMIN deve sempre indicare una sede; per il RESPONSABILE è già la sua.
        if (isNew && user?.role === UserRole.ADMIN && !servizio.sedeId) {
            addToast("Devi selezionare una Sede per il servizio", "error");
            return;
        }
        // Con il geofence attivo servono tutti i campi: altrimenti il BE lo disattiva
        // e l'utente non capirebbe perché non arrivano più alert.
        if (servizio.geofenceAttivo
                && (servizio.geofenceLat == null || servizio.geofenceLng == null
                    || !servizio.geofenceRaggioMt || servizio.geofenceRaggioMt <= 0)) {
            addToast("Geofence attivo: cerca un indirizzo o trascina il marker per posizionare il centro, e scegli un raggio.", "error");
            return;
        }

        try {
            // Normalizza i numerici prima dell'invio
            const payload: any = {
                ...servizio,
                clientId: Number(servizio.clientId),
                sedeId: servizio.sedeId ? Number(servizio.sedeId) : undefined
            };

            if (isNew) {
                const res = await ServizioFiduciarioService.create(payload);
                const newId = res.data?.id;
                addToast("Servizio creato con successo", "success");
                navigate(`/servizi/${newId}`);
            } else {
                await ServizioFiduciarioService.update(servizio.id!, payload);
                addToast("Servizio aggiornato", "success");
            }
        } catch (e) {
            addToast("Errore salvataggio", "error");
        }
    };

    // Tariffe collaboratori
    const handleAddTariffa = async () => {
        if (!newUser || !newRate || isNew) return;
        // Una sola tariffa per collaboratore e servizio: un duplicato rompe i calcoli
        // a valle (report ore, contabilità, generatore turni).
        if (tariffe.some(t => String(t.collaboratore?.id) === String(newUser))) {
            addToast("Collaboratore già presente in lista. Modifica la tariffa esistente.", "error");
            return;
        }
        try {
            const res = await ServizioFiduciarioService.addTariffa(servizio.id!, {
                collaboratoreId: Number(newUser),
                tariffaOraria: Number(newRate)
            });
            setTariffe([...tariffe, res.data || res]);
            setNewUser('');
            setNewRate('');
        } catch (e) {
            addToast("Errore aggiunta tariffa", "error");
        }
    };

    // Chi verrebbe aggiunto da "Aggiungi tutti": stessa regola del backend (costo orario
    // sicurezza > 0, non titolari, della sede del servizio se c'è, non già in lista).
    // Chi ha solo il costo investigazioni resta fuori.
    const sedeServizio = (servizio as any)?.sedeId;
    const candidatiSicurezza = utentiDb.filter((u: any) =>
        u.role !== 'ADMIN'
        && (!sedeServizio || (u.sedeIds || []).some((id: any) => String(id) === String(sedeServizio)))
        && Number(u.costoOrarioSicurezza) > 0
        && !tariffe.some(t => String(t.collaboratore?.id) === String(u.id)));
    const [addingTutti, setAddingTutti] = useState(false);
    const handleAddTutti = async () => {
        if (isNew || candidatiSicurezza.length === 0) return;
        const n = candidatiSicurezza.length;
        if (!window.confirm(`Aggiungere ${n} collaborator${n === 1 ? 'e' : 'i'} con la loro tariffa sicurezza?`)) return;
        setAddingTutti(true);
        try {
            const res: any = await ServizioFiduciarioService.addTariffeSicurezzaTutti(servizio.id!);
            const nuove: any[] = res?.data ?? res ?? [];
            setTariffe(prev => [...prev, ...nuove]);
            addToast(`${nuove.length} collaborator${nuove.length === 1 ? 'e aggiunto' : 'i aggiunti'}`, 'success');
        } catch {
            addToast('Errore aggiunta collaboratori', 'error');
        } finally {
            setAddingTutti(false);
        }
    };

    const handleRemoveTariffa = async (tariffaId: number) => {
        try {
            await ServizioFiduciarioService.removeTariffa(servizio.id!, tariffaId);
            setTariffe(tariffe.filter(t => t.id !== tariffaId));
        } catch (e) {
            addToast("Errore rimozione", "error");
        }
    };

    // Modifica inline della tariffa €/h di un collaboratore già assegnato.
    const startEditTariffa = (t: any) => {
        setEditingTariffaId(t.id);
        setEditTariffaValue(String(t.tariffaOraria ?? ''));
    };
    const cancelEditTariffa = () => {
        setEditingTariffaId(null);
        setEditTariffaValue('');
    };
    const saveEditTariffa = async (tariffaId: number) => {
        const v = Number(String(editTariffaValue).replace(',', '.'));
        if (isNaN(v) || v < 0) { addToast('Inserisci una tariffa valida (≥0)', 'error'); return; }
        setSavingTariffa(true);
        try {
            const res: any = await ServizioFiduciarioService.updateTariffa(servizio.id!, tariffaId, v);
            const updated = res?.data ?? res;
            setTariffe(tariffe.map(t => t.id === tariffaId ? { ...t, ...updated } : t));
            cancelEditTariffa();
        } catch { addToast('Errore aggiornamento tariffa', 'error'); }
        finally { setSavingTariffa(false); }
    };

    // Generazione turni
    const toggleGiorno = (giornoId: number) => {
        setRule(prev => ({
            ...prev,
            giorniSettimana: prev.giorniSettimana.includes(giornoId)
                ? prev.giorniSettimana.filter(g => g !== giornoId)
                : [...prev.giorniSettimana, giornoId]
        }));
    };

    const generaTurni = async () => {
        if (isNew) {
            addToast("Salva il servizio prima di generare turni", "error");
            return;
        }
        // Con un solo giorno (dal === al) i giorni della settimana non servono:
        // si genera un turno per quel giorno.
        const singleDay = !!rule.dataDal && rule.dataDal === rule.dataAl;
        if (!rule.userId || !rule.dataDal || !rule.dataAl || (!singleDay && rule.giorniSettimana.length === 0)) {
            addToast(singleDay ? "Compila operatore, giorno e orari" : "Compila tutti i campi del generatore (inclusi i giorni della settimana)", "error");
            return;
        }

        // Coerenza orario / notturno:
        // - non notturno: oraFine > oraInizio (stesso giorno)
        // - notturno: oraFine < oraInizio (a cavallo della mezzanotte); altrimenti sarebbe un turno di 24h+
        // Il confronto tra stringhe "HH:MM" equivale a quello orario.
        if (!rule.notturno && rule.oraFine <= rule.oraInizio) {
            addToast(
                "Orario fine deve essere successivo all'orario inizio. Se il turno è a cavallo di mezzanotte, attiva 'Turno notturno'.",
                "error",
            );
            return;
        }
        if (rule.notturno && rule.oraFine >= rule.oraInizio) {
            addToast(
                "Turno notturno: l'orario fine deve essere prima dell'orario inizio (es. 22:00 → 06:00).",
                "error",
            );
            return;
        }

        const nuoviTurni = [];

        // Scadenza in formato "YYYY-MM-DDTHH:mm:ss" per LocalDateTime; vuota → null.
        const scadenzaIso = rule.dataScadenzaAccettazione
            ? `${rule.dataScadenzaAccettazione}:00`
            : null;

        // I 4 orari della finestra vanno come "HH:mm" come oraInizio/oraFine; vuoti → null.
        const rangeInizioDa = rule.rangeInizioDa || null;
        const rangeInizioA = rule.rangeInizioA || null;
        const rangeFineDa = rule.rangeFineDa || null;
        const rangeFineA = rule.rangeFineA || null;

        // Date in ora locale (utils/turniDates): la conversione UTC/locale sbagliava
        // giorno con il cambio dell'ora legale.
        for (const dataTurno of generaDateTurni(rule.dataDal, rule.dataAl, rule.giorniSettimana, singleDay)) {
            nuoviTurni.push({
                collaboratoreId: Number(rule.userId),
                dataTurno,
                oraInizio: rule.oraInizio,
                oraFine: rule.oraFine,
                notturno: rule.notturno,
                rangeInizioDa,
                rangeInizioA,
                rangeFineDa,
                rangeFineA,
                dataScadenzaAccettazione: scadenzaIso,
            });
        }

        if (nuoviTurni.length === 0) {
            addToast("Nessun turno generabile in questo intervallo con i giorni selezionati.", "error");
            return;
        }

        const procedi = await avvisoCompresenze.conferma(servizio!.id!, Number(rule.userId),
            nuoviTurni.map(t => ({ dataTurno: t.dataTurno, oraInizio: t.oraInizio, oraFine: t.oraFine, notturno: !!t.notturno })));
        if (!procedi) return;

        try {
            await submitBulkTurni(nuoviTurni, false);
        } catch (e) {
            addToast("Errore generazione turni", "error");
        }
    };

    // Salvataggio massivo usato sia al primo invio sia al reinvio con forceOverride.
    const submitBulkTurni = async (
        turniPayload: any[],
        forceOverride: boolean,
    ) => {
        const res = await ServizioFiduciarioService.saveTurniBulk(servizio!.id!, turniPayload);
        const payload: any = (res as any)?.data ?? res;
        const created: any[] = Array.isArray(payload?.created)
            ? payload.created
            : (Array.isArray(payload) ? payload : []);
        const conflicts: ConflittoDisponibilita[] = Array.isArray(payload?.conflicts)
            ? payload.conflicts
            : [];
        setTurni(prev => [...prev, ...created]);
        if (conflicts.length > 0) {
            const cid = turniPayload[0]?.collaboratoreId;
            const collab: any = (utentiDb as any[]).find((u: any) => String(u.id) === String(cid));
            setConflittiTurni({
                list: conflicts,
                collab: collab ? `${collab.name} ${collab.surname || ''}`.trim() : undefined,
                creati: created.length,
                totale: turniPayload.length,
            });
            // Come per gli incarichi, dopo un invio forzato non riproponiamo l'override:
            // il turno appena creato diventerebbe un nuovo conflitto e si andrebbe in loop.
            const someOverridable = !forceOverride && conflicts.some(c => (c as any).overridable);
            setPendingTurniPayload(someOverridable ? turniPayload : null);
            addToast(`Generati ${created.length} su ${turniPayload.length} turni; ${conflicts.length} saltati per conflitto.`, 'info');
        } else {
            setPendingTurniPayload(null);
            addToast(`Generati e salvati ${created.length} turni${forceOverride ? ' (override applicato)' : ''}!`, 'success');
        }
    };

    // Chiamato dalla modale conflitti con le righe da forzare. Ogni conflitto porta
    // richiestaData/oraInizio/oraFine/notturno per ricostruire la riga originale,
    // che viene reinviata con forceOverride=true.
    const handleConfermaOverrideTurni = async (selezionati: ConflittoDisponibilita[]) => {
        if (!pendingTurniPayload || selezionati.length === 0) return;
        const chiavi = new Set(
            selezionati.map((c: any) =>
                [c.richiestaData, c.richiestaOraInizio, c.richiestaOraFine, !!c.richiestaNotturno].join('|')
            )
        );
        const replay = pendingTurniPayload
            .filter((row: any) => {
                const k = [
                    row.dataTurno,
                    row.oraInizio,
                    row.oraFine,
                    !!row.notturno,
                ].join('|');
                return chiavi.has(k);
            })
            .map((row: any) => ({ ...row, forceOverride: true }));
        if (replay.length === 0) {
            addToast("Nessuna riga selezionata per l'override.", 'info');
            return;
        }
        setConflittiTurni(null);
        try { await submitBulkTurni(replay, true); }
        catch { addToast("Errore durante l'override", 'error'); }
    };

    // Conferma revoca/eliminazione turno con una modale.
    const [confermaAzione, setConfermaAzione] = useState<{ tipo: 'revoca' | 'elimina'; turno: any } | null>(null);
    const [confermaSaving, setConfermaSaving] = useState(false);

    const eliminaTurno = async (turnoId: number) => {
        try {
            await ServizioFiduciarioService.deleteTurno(servizio.id!, turnoId);
            setTurni(turni.filter(t => t.id !== turnoId));
            addToast("Turno eliminato", "success");
        } catch(e: any) {
            const msg = e?.response?.data?.message || "Errore eliminazione turno";
            addToast(msg, "error");
        }
    };

    // Revoca turno (soft, mantiene lo storico); il BE la blocca se il turno è già avviato.
    // La modale scadenza parte dal valore esistente in formato datetime-local.
    const openScadenzaModal = (t: any) => {
        setScadenzaTurno(t);
        const v = t.dataScadenzaAccettazione;
        if (v) {
            const d = new Date(v);
            const pad = (n: number) => String(n).padStart(2, '0');
            setScadenzaValue(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
        } else {
            setScadenzaValue('');
        }
    };
    const saveScadenza = async (clear = false) => {
        if (!scadenzaTurno) return;
        const iso = clear ? null : (scadenzaValue ? `${scadenzaValue}:00` : null);
        setScadenzaSaving(true);
        try {
            const res: any = await ServizioFiduciarioService.setScadenzaAccettazione(scadenzaTurno.id, iso);
            const upd = res?.data || res;
            setTurni(prev => prev.map(t => t.id === scadenzaTurno.id ? { ...t, dataScadenzaAccettazione: upd?.dataScadenzaAccettazione ?? iso } : t));
            addToast(clear ? 'Scadenza rimossa' : 'Scadenza impostata', 'success');
            setScadenzaTurno(null);
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Errore salvataggio scadenza';
            addToast(msg, 'error');
        } finally {
            setScadenzaSaving(false);
        }
    };

    // Modale orari turno: non avviato → giorno, inizio e fine; avviato → solo la fine.
    const turnoAvviato = (t: any) => !!t?.effettivoInizio || (t?.sessioni?.length ?? 0) > 0;
    const openEditFineModal = (t: any) => {
        setEditFineError(null);
        setEditConflitti(null);
        setEditConferma(false);
        setEditFineValue(t.oraFine ? String(t.oraFine).slice(0, 5) : '');
        setEditInizioValue(t.oraInizio ? String(t.oraInizio).slice(0, 5) : '');
        setEditDataTurno(t.dataTurno ? String(t.dataTurno).slice(0, 10) : '');
        setEditFineTurno(t);
    };
    // Cambiando un orario la sovrapposizione mostrata non vale più.
    const cambiaOrarioTurno = (setter: (v: string) => void, v: string) => {
        setter(v);
        setEditConflitti(null);
        setEditConferma(false);
    };
    const saveEditFine = async () => {
        if (!editFineTurno) return;
        const soloFine = turnoAvviato(editFineTurno);
        if (!editFineValue || (!soloFine && (!editInizioValue || !editDataTurno))) {
            setEditFineError(soloFine ? 'Inserisci l\'ora di fine.' : 'Inserisci giorno, inizio e fine.');
            return;
        }
        // Al primo invio (non alla conferma dopo un conflitto) avviso se il servizio è già coperto.
        if (!editConflitti && servizio?.id) {
            const t: any = editFineTurno;
            const procedi = await avvisoCompresenze.conferma(servizio.id, t.collaboratoreId ?? t.collaboratore?.id, [{
                dataTurno: soloFine ? String(t.dataTurno).slice(0, 10) : editDataTurno,
                oraInizio: soloFine ? String(t.oraInizio).slice(0, 5) : editInizioValue,
                oraFine: editFineValue,
            }], t.id);
            if (!procedi) return;
        }
        setEditFineSaving(true); setEditFineError(null);
        try {
            const res: any = soloFine
                ? await ServizioFiduciarioService.updateOraFineTurno(editFineTurno.id, editFineValue)
                : await ServizioFiduciarioService.updateOrariTurno(editFineTurno.id, {
                    dataTurno: editDataTurno,
                    oraInizio: editInizioValue,
                    oraFine: editFineValue,
                    forceOverride: !!editConflitti?.overridable && editConferma,
                });
            const upd = res?.data ?? res;
            setTurni(prev => prev.map(t => t.id === editFineTurno.id ? { ...t, ...upd } : t));
            addToast(soloFine ? 'Ora di fine aggiornata' : 'Orari del turno aggiornati', 'success');
            setEditFineTurno(null);
        } catch (e: any) {
            const c = estraiConflitti(e);
            if (c) {
                setEditConflitti(c);
                setEditConferma(false);
            } else {
                setEditFineError(e?.response?.data?.message || 'Errore modifica orari del turno');
            }
        } finally {
            setEditFineSaving(false);
        }
    };

    // Modale modifica finestra di tolleranza su un turno esistente.
    const hhmm = (v: any) => (v ? String(v).slice(0, 5) : '');
    const openEditRangeModal = (t: any) => {
        setEditRangeError(null);
        setEditRange({
            rangeInizioDa: hhmm(t.rangeInizioDa), rangeInizioA: hhmm(t.rangeInizioA),
            rangeFineDa: hhmm(t.rangeFineDa), rangeFineA: hhmm(t.rangeFineA),
        });
        setEditRangeTurno(t);
    };
    const saveEditRange = async () => {
        if (!editRangeTurno) return;
        setEditRangeSaving(true); setEditRangeError(null);
        try {
            const res: any = await ServizioFiduciarioService.updateRangeTurno(editRangeTurno.id, {
                rangeInizioDa: editRange.rangeInizioDa || null,
                rangeInizioA: editRange.rangeInizioA || null,
                rangeFineDa: editRange.rangeFineDa || null,
                rangeFineA: editRange.rangeFineA || null,
            });
            const upd = res?.data ?? res;
            setTurni(prev => prev.map(t => t.id === editRangeTurno.id ? { ...t, ...upd } : t));
            addToast('Finestra di tolleranza aggiornata', 'success');
            setEditRangeTurno(null);
        } catch (e: any) {
            setEditRangeError(e?.response?.data?.message || 'Errore modifica range');
        } finally {
            setEditRangeSaving(false);
        }
    };

    // Disattiva il controllo geofence solo su questo turno (es. turno occasionale
    // in un'altra sede). Vale per gli start/stop successivi.
    const toggleGeofenceDisabilitato = async (turnoId: number, disabilitato: boolean) => {
        try {
            const res: any = await ServizioFiduciarioService.setGeofenceDisabilitato(turnoId, disabilitato);
            const updated: any = res.data ?? res;
            setTurni(turni.map(t => t.id === turnoId ? { ...t, ...updated } : t));
            addToast(disabilitato ? "Geofence disattivato per questo turno" : "Geofence riattivato per questo turno", "success");
        } catch {
            addToast("Errore aggiornamento geofence turno", "error");
        }
    };

    const revocaTurno = async (turnoId: number) => {
        try {
            const res: any = await ServizioFiduciarioService.revocaTurno(turnoId);
            const updated = res?.data ?? res;
            setTurni(turni.map(t => t.id === turnoId ? { ...t, ...updated } : t));
            addToast("Turno revocato", "success");
        } catch (e: any) {
            const msg = e?.response?.data?.message || "Errore revoca turno";
            addToast(msg, "error");
        }
    };

    // Helper cliente: per le aziende "Acme S.r.l. (Mario Rossi)", per i privati
    // nome e cognome, come in utils/clientDisplay.
    const getClientLabel = (c: any) => {
        if (!c) return '';
        const ragione = getClientRagioneSociale(c);
        const referente = getClientReferente(c);
        if (isClienteAzienda(c)) {
            if (ragione && referente) return `${ragione} (${referente})`;
            return ragione || referente || getClientDisplayName(c);
        }
        return getClientDisplayName(c);
    };
    const getClientType = (c: any) => isClienteAzienda(c) ? 'AZIENDA' : 'PRIVATO';
    const selectedClient = clientiDb.find(c => String(c.id) === String(servizio.clientId));

    // La ricerca guarda nome persona, ragione sociale (anche denominazioneTerzi), P.IVA e CF.
    // Solo i clienti della sede del servizio (o di quella selezionata): niente dati di altre sedi.
    const sedeClienti = servizio.sedeId != null && servizio.sedeId !== '' ? Number(servizio.sedeId) : currentSedeId;
    const filteredClients = clientiDellaSede(clientiDb, sedeClienti, user?.role === UserRole.ADMIN).filter(c => {
        if (!clientSearchTerm.trim()) return true;
        const q = clientSearchTerm.toLowerCase();
        const fields = [
            c.nome, c.surname, c.ragioneSociale,
            (c as any).denominazioneTerzi,
            c.partitaIva, c.codiceFiscale,
            getClientLabel(c)
        ];
        return fields.some(v => v && String(v).toLowerCase().includes(q));
    });

    // Creazione cliente inline
    const openCreateClient = () => setIsCreateClientOpen(true);

    // Chiamato da ClienteCreateModal a cliente già salvato: lo aggiungiamo alla
    // lista e lo selezioniamo.
    const handleClientCreated = (created: any) => {
        if (!created?.id) return;
        setClientiDb(prev => [...prev, created]);
        setServizio((prev: any) => ({ ...prev, clientId: String(created.id) }));
        setIsCreateClientOpen(false);
        setIsClientDropdownOpen(false);
        setClientSearchTerm('');
    };

    // Sede del cliente: quella del servizio, poi la sede corrente (admin), poi quella del responsabile.
    const sedeIdForNewClient: number | undefined =
        servizio.sedeId ? Number(servizio.sedeId)
        : (typeof currentSedeId === 'number' ? currentSedeId
        : (user?.sedeId ?? undefined));

    if (isLoading) return <div className="p-12 text-center text-gray-500 font-medium animate-pulse">Caricamento in corso...</div>;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-6 pb-20">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/servizi')} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"><ArrowLeft size={20}/></button>
                    <div className="w-14 h-14 bg-emerald-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-200">
                        <Shield size={28} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{isNew ? 'Nuovo Servizio Fiduciario' : 'Gestione Servizio'}</h1>
                        <p className="text-gray-500">Configura postazione, tariffe e turnazioni fisse.</p>
                    </div>
                </div>
                <button onClick={onSaveServizio} disabled={savingBase} className="bg-emerald-600 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-emerald-700 flex items-center gap-2 shadow-lg shadow-emerald-200 transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                    {savingBase ? <Loader2 size={20} className="animate-spin"/> : <Save size={20}/>} Salva Dati Base
                </button>
            </div>

            {/* Tab (visibili solo dopo il primo salvataggio) */}
            {!isNew && (
                <div className="flex gap-2 border-b border-slate-200">
                    <button
                        onClick={() => setActiveTab('pianificazione')}
                        className={`px-4 py-2 text-sm font-bold flex items-center gap-2 border-b-2 -mb-px transition-colors ${activeTab === 'pianificazione' ? 'text-emerald-700 border-emerald-600' : 'text-slate-500 border-transparent hover:text-slate-700'}`}>
                        <Calendar size={16}/> Pianificazione
                    </button>
                    <button
                        onClick={() => setActiveTab('report')}
                        className={`px-4 py-2 text-sm font-bold flex items-center gap-2 border-b-2 -mb-px transition-colors ${activeTab === 'report' ? 'text-blue-700 border-blue-600' : 'text-slate-500 border-transparent hover:text-slate-700'}`}>
                        <FileText size={16}/> Report
                    </button>
                    <button
                        onClick={() => setActiveTab('contabilita')}
                        className={`px-4 py-2 text-sm font-bold flex items-center gap-2 border-b-2 -mb-px transition-colors ${activeTab === 'contabilita' ? 'text-emerald-700 border-emerald-600' : 'text-slate-500 border-transparent hover:text-slate-700'}`}>
                        <Receipt size={16}/> Contabilità
                    </button>
                </div>
            )}

            {(isNew || activeTab === 'pianificazione') && (
            // items-start evita che le colonne si allunghino alla riga più alta: con la
            // mappa geofence la sinistra cresce molto e a destra resterebbe spazio vuoto.
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

                {/* Colonna sinistra */}
                <div className="space-y-6">
                    {/*
                      Sede: picker per l'ADMIN, anche in modifica. Per il RESPONSABILE in modifica
                      la sede è mostrata in sola lettura.
                    */}
                    {showSedePicker ? (
                        <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                            <div className="flex items-center gap-2 text-orange-800 font-bold mb-2 text-sm">
                                <Building2 size={16} /> Sede del Servizio
                            </div>
                            <select
                                className="w-full p-2.5 border border-orange-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-orange-500"
                                value={servizio.sedeId}
                                onChange={e => setServizio({...servizio, sedeId: e.target.value})}
                                required
                            >
                                <option value="">-- Seleziona Sede --</option>
                                {sedi.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                            <p className="text-[11px] text-orange-700/80 mt-2">
                                Il servizio è assegnato a questa sede per fatturazione e visibilità.
                            </p>
                        </div>
                    ) : (!isNew && servizio.sedeId && (
                        <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 flex items-center gap-2 text-slate-700 text-sm">
                            <Building2 size={16} className="text-slate-500"/>
                            <span className="font-bold text-xs text-slate-500 uppercase">Sede:</span>
                            <span className="font-semibold">
                                {sedi.find(s => String(s.id) === String(servizio.sedeId))?.name || `Sede #${servizio.sedeId}`}
                            </span>
                        </div>
                    ))}

                    {/* Info servizio */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b pb-2"><MapPin size={20} className="text-emerald-600"/> Dettagli Postazione</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome Servizio / Sito</label>
                                <input className="w-full p-2.5 border rounded-lg font-bold focus:ring-2 focus:ring-emerald-500 outline-none" value={servizio.nome} onChange={e => setServizio({...servizio, nome: e.target.value})} placeholder="Es. Esselunga Via Roma" />
                            </div>

                            {/* Selettore cliente */}
                            <div className="relative" ref={dropdownRef}>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Cliente Fatturazione</label>
                                <button
                                    type="button"
                                    onClick={() => setIsClientDropdownOpen(!isClientDropdownOpen)}
                                    className="w-full p-2.5 border rounded-lg flex items-center justify-between bg-white focus:ring-2 focus:ring-emerald-500 outline-none"
                                >
                                    {selectedClient ? (
                                        <div className="flex items-center gap-2 min-w-0">
                                            {isClienteAzienda(selectedClient) ? <Building2 size={16} className="text-emerald-600 flex-shrink-0" /> : <UserIcon size={16} className="text-emerald-600 flex-shrink-0" />}
                                            <span className="font-medium text-gray-900 truncate">{getClientLabel(selectedClient)}</span>
                                        </div>
                                    ) : (
                                        <span className="text-gray-400 truncate">-- Seleziona o Cerca Cliente --</span>
                                    )}
                                    <ChevronDown size={18} className="text-gray-400 flex-shrink-0 ml-2" />
                                </button>

                                {isClientDropdownOpen && (
                                    <div className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden">
                                        <div className="p-2 border-b bg-slate-50 relative">
                                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                            <input
                                                autoFocus
                                                type="text"
                                                className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                                placeholder="Cerca cliente..."
                                                value={clientSearchTerm}
                                                onChange={(e) => setClientSearchTerm(e.target.value)}
                                            />
                                        </div>
                                        <div className="max-h-60 overflow-y-auto">
                                            {filteredClients.length === 0 ? (
                                                <div className="p-4 text-center text-sm text-gray-500 space-y-3">
                                                    <p>Nessun cliente trovato.</p>
                                                    <button
                                                        type="button"
                                                        onClick={openCreateClient}
                                                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-md font-medium hover:bg-emerald-700 transition-colors"
                                                    >
                                                        <UserPlus size={14}/> Crea nuovo cliente
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    {filteredClients.map(c => {
                                                        const isAzienda = isClienteAzienda(c);
                                                        return (
                                                            <button
                                                                key={c.id}
                                                                className={`w-full text-left p-3 hover:bg-emerald-50 flex items-center justify-between border-b border-gray-50 transition-colors ${String(c.id) === String(servizio.clientId) ? 'bg-emerald-50/50' : ''}`}
                                                                onClick={() => {
                                                                    setServizio({...servizio, clientId: String(c.id)});
                                                                    setIsClientDropdownOpen(false);
                                                                    setClientSearchTerm('');
                                                                }}
                                                            >
                                                                <div className="flex items-center gap-2 overflow-hidden">
                                                                    {isAzienda ? <Building2 size={16} className="text-emerald-500 shrink-0" /> : <UserIcon size={16} className="text-emerald-500 shrink-0" />}
                                                                    <span className="font-medium text-gray-800 text-sm truncate">{getClientLabel(c)}</span>
                                                                </div>
                                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase shrink-0 ${isAzienda ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                                                    {isAzienda ? 'Azienda' : 'Privato'}
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                    <button
                                                        type="button"
                                                        onClick={openCreateClient}
                                                        className="w-full text-left p-3 text-emerald-700 hover:bg-emerald-50 flex items-center gap-2 border-t border-gray-100 font-medium text-sm"
                                                    >
                                                        <UserPlus size={16}/> Crea nuovo cliente…
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Indirizzo Sede Servizio</label>
                                <input className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none" value={servizio.indirizzo} onChange={e => setServizio({...servizio, indirizzo: e.target.value})} placeholder="Via delle Industrie 10..." />
                            </div>
                            {/*
                              Prezzo di vendita: tariffa oraria (default) o forfait di contratto, che dà un
                              ricavo mensile fisso indipendente dalle ore.
                            */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Modalità Prezzo Vendita</label>
                                <div className="flex gap-2 mb-2">
                                    <button
                                        type="button"
                                        onClick={() => setServizio({...servizio, tipoTariffaCliente: 'ORARIA'})}
                                        className={`flex-1 px-3 py-2 rounded-lg border text-xs font-bold transition-colors ${
                                            servizio.tipoTariffaCliente === 'ORARIA'
                                                ? 'bg-green-50 border-green-300 text-green-700'
                                                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                                        }`}
                                    >
                                        Tariffa oraria
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setServizio({...servizio, tipoTariffaCliente: 'FORFAIT'})}
                                        className={`flex-1 px-3 py-2 rounded-lg border text-xs font-bold transition-colors ${
                                            servizio.tipoTariffaCliente === 'FORFAIT'
                                                ? 'bg-green-50 border-green-300 text-green-700'
                                                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                                        }`}
                                    >
                                        Forfait contratto
                                    </button>
                                </div>
                                {servizio.tipoTariffaCliente === 'FORFAIT' ? (
                                    <>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Prezzo Forfait Contratto (€)</label>
                                        <NumberInput
                                            min={0} step={0.01}
                                            className="w-full p-2.5 border border-green-200 rounded-lg bg-green-50 text-green-700 font-bold focus:ring-2 focus:ring-green-500 outline-none"
                                            value={servizio.prezzoForfaitCliente}
                                            onChange={e => setServizio({...servizio, prezzoForfaitCliente: Number(e.target.value)})}
                                        />
                                        <p className="text-[10px] text-gray-400 mt-1">
                                            Ricavo cliente fisso per ogni mese del contratto, indipendente dalle ore svolte.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Prezzo Orario Vendita (€/h)</label>
                                        <NumberInput
                                            min={0} className="w-full p-2.5 border border-green-200 rounded-lg bg-green-50 text-green-700 font-bold focus:ring-2 focus:ring-green-500 outline-none"
                                            value={servizio.tariffaOrariaCliente}
                                            onChange={e => setServizio({...servizio, tariffaOrariaCliente: Number(e.target.value)})}
                                        />
                                    </>
                                )}
                            </div>

                            <div className="pt-2">
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Stato Servizio</label>
                                <select className="w-full p-2.5 border rounded-lg bg-slate-50" value={servizio.stato} onChange={e => setServizio({...servizio, stato: e.target.value})}>
                                    <option value="Attivo">Attivo</option>
                                    <option value="Sospeso">Sospeso</option>
                                    <option value="Terminato">Terminato</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/*
                      Tariffe collaboratori: sempre visibili ma disabilitate in creazione, serve
                      l'id del servizio.
                    */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b pb-2"><DollarSign size={20} className="text-emerald-600"/> Tariffe Collaboratori</h3>

                        {isNew && (
                            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-xs">
                                Salva prima i dati base del servizio per poter aggiungere tariffe per collaboratore.
                            </div>
                        )}

                        <div className="flex gap-2 mb-4 bg-slate-50 p-3 rounded-lg border">
                            <select disabled={isNew} className="flex-1 min-w-0 p-2 border rounded bg-white text-sm focus:ring-2 focus:ring-emerald-500 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed" value={newUser} onChange={e => {
                                const uid = e.target.value;
                                setNewUser(uid);
                                // Precompila la tariffa con quella del profilo del collaboratore
                                // (costoOrarioSicurezza, altrimenti costoOrario). L'admin può modificarla.
                                const u: any = utentiDb.find(x => String(x.id) === String(uid));
                                if (u) {
                                    const rate = Number(u.costoOrarioSicurezza) || Number(u.costoOrario) || 0;
                                    setNewRate(rate > 0 ? String(rate) : '');
                                } else {
                                    setNewRate('');
                                }
                            }}>
                                <option value="">- Operatore -</option>
                                {utentiDb
                                    .filter(u => !tariffe.some(t => String(t.collaboratore?.id) === String(u.id)))
                                    .map(u => <option key={u.id} value={u.id}>{u.name} {u.surname}</option>)}
                            </select>
                            <NumberInput disabled={isNew}
                                   min={0} className="w-20 shrink-0 p-2 border rounded bg-white text-sm focus:ring-2 focus:ring-emerald-500 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="€/ora" value={newRate} onChange={e => setNewRate(e.target.value)} />
                            <button disabled={isNew} onClick={handleAddTariffa} className="bg-emerald-600 text-white px-3 shrink-0 rounded hover:bg-emerald-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"><Plus size={18}/></button>
                        </div>
                        {!isNew && (
                            <button
                                onClick={handleAddTutti}
                                disabled={addingTutti || candidatiSicurezza.length === 0}
                                className="w-full mb-4 flex items-center justify-center gap-2 text-sm font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-2 hover:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Aggiunge tutti i collaboratori che hanno un costo orario sicurezza nella loro scheda"
                            >
                                {addingTutti ? <Loader2 size={16} className="animate-spin"/> : <Users size={16}/>}
                                Aggiungi tutti con tariffa sicurezza ({candidatiSicurezza.length})
                            </button>
                        )}

                        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                            {tariffe.length === 0 ? (
                                <div className="text-sm text-gray-400 text-center py-4">Nessun collaboratore abilitato.</div>
                            ) : (
                                tariffe.map(t => {
                                    const isEditing = editingTariffaId === t.id;
                                    return (
                                        <div key={t.id} className="flex justify-between items-center p-3 bg-white border border-gray-100 rounded-lg shadow-sm hover:border-emerald-100 transition-colors">
                                            <span className="font-bold text-sm text-gray-700 truncate">{t.collaboratore?.name} {t.collaboratore?.surname}</span>
                                            <div className="flex items-center gap-2">
                                                {isEditing ? (
                                                    <>
                                                        <div className="flex items-center border border-emerald-300 rounded overflow-hidden">
                                                            <span className="px-2 text-xs text-emerald-700 bg-emerald-50 border-r border-emerald-200 py-1">€</span>
                                                            <NumberInput
                                                                min={0} step={0.01}
                                                                value={editTariffaValue}
                                                                onChange={e => setEditTariffaValue(e.target.value)}
                                                                onKeyDown={e => { if (e.key === 'Enter') saveEditTariffa(t.id); if (e.key === 'Escape') cancelEditTariffa(); }}
                                                                autoFocus
                                                                className="w-20 px-2 py-1 text-sm font-mono text-right outline-none"
                                                            />
                                                            <span className="px-2 text-xs text-gray-400 bg-gray-50 border-l border-gray-200 py-1">/h</span>
                                                        </div>
                                                        <button onClick={() => saveEditTariffa(t.id)} disabled={savingTariffa} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded">
                                                            {savingTariffa ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle size={14}/>}
                                                        </button>
                                                        <button onClick={cancelEditTariffa} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded"><X size={14}/></button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => startEditTariffa(t)}
                                                            className="group text-sm font-mono font-bold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 rounded border border-emerald-100 transition-colors inline-flex items-center gap-1"
                                                            title="Click per modificare la tariffa"
                                                        >
                                                            € {t.tariffaOraria}/h
                                                            <Pencil size={10} className="opacity-0 group-hover:opacity-100 text-emerald-700"/>
                                                        </button>
                                                        <button onClick={() => handleRemoveTariffa(t.id)} className="text-red-400 hover:text-red-600 p-1 bg-red-50 rounded transition-colors"><Trash2 size={16}/></button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Colonna destra */}
                <div className="lg:col-span-2 space-y-6">
                    {isNew ? (
                        <div className="bg-emerald-50 border border-emerald-100 text-emerald-700 p-12 rounded-xl text-center shadow-inner flex flex-col items-center justify-center h-full">
                            <Shield className="mx-auto mb-4 opacity-40 text-emerald-500" size={64}/>
                            <p className="font-bold text-xl">Salva il Servizio per procedere</p>
                            <p className="text-emerald-600 opacity-80 mt-2 max-w-sm">Dopo aver salvato i dati base potrai aggiungere gli operatori autorizzati e generare massivamente il calendario dei turni.</p>
                        </div>
                    ) : (
                        <>
                            {/* Generatore massivo */}
                            <div className="bg-gradient-to-br from-emerald-50 to-white p-6 rounded-xl shadow-sm border border-emerald-100">
                                <h3 className="font-bold text-lg mb-4 flex items-center gap-2 border-b border-emerald-100 pb-2 text-emerald-900"><Calendar size={20} className="text-emerald-600"/> Generatore Turni Ripetitivi</h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-xs font-bold text-emerald-800 uppercase mb-1">1. Assegna a:</label>
                                        <select className="w-full p-2.5 border border-emerald-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 outline-none" value={rule.userId} onChange={e => setRule({...rule, userId: e.target.value})}>
                                            <option value="">-- Seleziona tra abilitati --</option>
                                            {tariffe.map(t => <option key={t.collaboratore?.id} value={t.collaboratore?.id}>{t.collaboratore?.name} (Tariffa: €{t.tariffaOraria}/h)</option>)}
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-xs font-bold text-emerald-800 uppercase mb-1">Dal giorno</label>
                                            {/*
                                              Compila "Al giorno" con lo stesso giorno (turno singolo di default), senza
                                              sovrascrivere una data successiva già impostata.
                                            */}
                                            <input type="date" className="w-full p-2.5 border border-emerald-200 rounded-lg bg-white text-sm focus:ring-2 focus:ring-emerald-500 outline-none" value={rule.dataDal} onChange={e => { const v = e.target.value; setRule(r => ({ ...r, dataDal: v, dataAl: (!r.dataAl || r.dataAl < v) ? v : r.dataAl })); }} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-emerald-800 uppercase mb-1">Al giorno</label>
                                            <input type="date" className="w-full p-2.5 border border-emerald-200 rounded-lg bg-white text-sm focus:ring-2 focus:ring-emerald-500 outline-none" value={rule.dataAl} onChange={e => setRule({...rule, dataAl: e.target.value})} />
                                        </div>
                                    </div>
                                </div>

                                {/* I giorni della settimana servono solo su un intervallo di più giorni. */}
                                {rule.dataDal !== rule.dataAl && (
                                <div className="mb-5">
                                    <label className="block text-xs font-bold text-emerald-800 uppercase mb-2">2. In quali giorni della settimana?</label>
                                    <div className="flex flex-wrap gap-2">
                                        {GIORNI.map(g => (
                                            <button
                                                key={g.id}
                                                onClick={() => toggleGiorno(g.id)}
                                                className={`px-4 py-2 rounded-lg text-sm font-bold border transition-colors ${rule.giorniSettimana.includes(g.id) ? 'bg-emerald-600 text-white border-emerald-600 shadow-md transform scale-105' : 'bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50'}`}
                                            >
                                                {g.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                )}

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
                                    <div>
                                        <label className="block text-xs font-bold text-emerald-800 uppercase mb-1">Orario Inizio</label>
                                        <input type="time" className="w-full p-2.5 border border-emerald-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 outline-none" value={rule.oraInizio} onChange={e => setRule({...rule, oraInizio: e.target.value})} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-emerald-800 uppercase mb-1">Orario Fine</label>
                                        <input type="time" className="w-full p-2.5 border border-emerald-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 outline-none" value={rule.oraFine} onChange={e => setRule({...rule, oraFine: e.target.value})} />
                                    </div>
                                    <div className="md:col-span-2">
                                        <button onClick={onGeneraTurni} disabled={generandoTurni} className="w-full bg-emerald-600 text-white p-2.5 rounded-lg font-bold hover:bg-emerald-700 shadow-lg shadow-emerald-200 flex items-center justify-center gap-2 transition-transform active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed">
                                            {generandoTurni ? <Loader2 size={18} className="animate-spin"/> : <Play size={18}/>} Salva e Genera Turni
                                        </button>
                                    </div>
                                </div>
                                <div className="flex justify-end">
                                    <button type="button" onClick={() => setShowAPosteriori(true)}
                                            className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 flex items-center gap-1.5">
                                        <History size={16}/> Inserisci turno già svolto
                                    </button>
                                </div>
                                {showAPosteriori && (
                                    <LavoroAPosterioriModal
                                        mode="turno"
                                        servizioId={Number(id)}
                                        collaboratori={tariffe.filter(t => t.collaboratore?.id).map(t => ({ id: t.collaboratore.id, nome: t.collaboratore.name }))}
                                        onClose={() => setShowAPosteriori(false)}
                                        onSaved={() => caricaServizio()}
                                    />
                                )}

                                {/*
                                  Senza la spunta notturno non si salvano turni con oraFine < oraInizio. Con la
                                  spunta oraFine è del giorno dopo (es. 22:00 → 06:00).
                                */}
                                <div className="mt-4 flex items-start gap-3 bg-emerald-100/60 border border-emerald-200 rounded-lg p-3">
                                    <input
                                        id="rule-notturno"
                                        type="checkbox"
                                        className="mt-0.5 w-4 h-4 accent-emerald-600 cursor-pointer"
                                        checked={rule.notturno}
                                        onChange={e => setRule({ ...rule, notturno: e.target.checked })}
                                    />
                                    <label htmlFor="rule-notturno" className="text-sm text-emerald-900 cursor-pointer select-none">
                                        <span className="font-bold">Turno notturno</span> (a cavallo di mezzanotte)
                                        <span className="block text-xs text-emerald-700 mt-0.5 font-normal">
                                            Quando attivo, l'orario fine si riferisce al giorno successivo. Esempio: inizio 22:00, fine 06:00 = turno di 8 ore.
                                        </span>
                                    </label>
                                </div>

                                {/* Finestra di tolleranza opzionale su inizio/fine: se vuota non si invia. */}
                                <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                                    <p className="text-xs font-bold text-emerald-800 uppercase mb-2">
                                        Range di tolleranza <span className="text-emerald-500 font-normal normal-case">(opzionale)</span>
                                    </p>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div>
                                            <label className="block text-[11px] font-semibold text-emerald-700 mb-1">Inizio · da</label>
                                            <input type="time" className="w-full p-2.5 border border-emerald-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 outline-none" value={rule.rangeInizioDa} onChange={e => setRule({ ...rule, rangeInizioDa: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold text-emerald-700 mb-1">Inizio · a</label>
                                            <input type="time" className="w-full p-2.5 border border-emerald-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 outline-none" value={rule.rangeInizioA} onChange={e => setRule({ ...rule, rangeInizioA: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold text-emerald-700 mb-1">Fine · da</label>
                                            <input type="time" className="w-full p-2.5 border border-emerald-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 outline-none" value={rule.rangeFineDa} onChange={e => setRule({ ...rule, rangeFineDa: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-semibold text-emerald-700 mb-1">Fine · a</label>
                                            <input type="time" className="w-full p-2.5 border border-emerald-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 outline-none" value={rule.rangeFineA} onChange={e => setRule({ ...rule, rangeFineA: e.target.value })} />
                                        </div>
                                    </div>
                                    <p className="text-[11px] text-emerald-600 mt-2">
                                        Se compilati, definiscono la finestra entro cui è accettato l'inizio (es. 07:45–08:15) e la fine (es. 09:45–10:15). Lasciali vuoti per disattivare la tolleranza.
                                    </p>
                                </div>

                                {/*
                                  Scadenza di accettazione per tutti i turni generati: allo scadere lo scheduler
                                  porta quelli ancora ASSEGNATO a RIFIUTATO. Vuoto = nessuna scadenza.
                                */}
                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                                    <div>
                                        <label className="block text-xs font-bold text-emerald-800 uppercase mb-1">
                                            Scadenza accettazione (opzionale)
                                        </label>
                                        <input
                                            type="datetime-local"
                                            className="w-full p-2.5 border border-emerald-200 rounded-lg bg-white text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                                            value={rule.dataScadenzaAccettazione}
                                            onChange={e => setRule({ ...rule, dataScadenzaAccettazione: e.target.value })}
                                        />
                                    </div>
                                    <p className="text-[11px] text-emerald-700">
                                        Se valorizzata, ogni turno generato dovrà essere accettato dal
                                        collaboratore entro questa data e ora; alla scadenza viene
                                        automaticamente rifiutato.
                                    </p>
                                </div>
                            </div>

                            {/* Lista turni */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-[400px]">
                                <div className="bg-slate-50 border-b p-4 flex justify-between items-center shrink-0">
                                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                                        <Clock className="text-emerald-500" size={18}/>
                                        Pianificazione Turni ({turni.length})
                                    </h3>
                                </div>
                                <div className="flex-1 overflow-y-auto">
                                    {turni.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                            <Calendar size={48} className="opacity-20 mb-3" />
                                            <p className="font-medium">Nessun turno salvato.</p>
                                        </div>
                                    ) : (
                                        <table className="w-full text-left text-sm">
                                            <thead className="bg-white sticky top-0 border-b shadow-sm z-10">
                                            <tr className="text-xs uppercase text-gray-500 font-bold">
                                                <th className="p-3">Data</th>
                                                <th className="p-3">Orario</th>
                                                <th className="p-3">Operatore</th>
                                                <th className="p-3 text-center">Stato</th>
                                                <th className="p-3 text-center">Azioni</th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                            {/*
                                              Ordine crescente per dataTurno e poi oraInizio, come in TabContabilitaServizio.
                                              localeCompare funziona perché ISO e "HH:MM" sono ordinabili come stringhe.
                                            */}
                                            {[...turni].sort((a, b) => {
                                                const cmpData = String(a.dataTurno).localeCompare(String(b.dataTurno));
                                                if (cmpData !== 0) return cmpData;
                                                return String(a.oraInizio || '').localeCompare(String(b.oraInizio || ''));
                                            }).map(t => {
                                                const isRevocato = !!t.dataRevoca;
                                                const isRifiutato = t.statoAccettazione === 'RIFIUTATO';
                                                const isAccettato = t.statoAccettazione === 'ACCETTATO';
                                                const isAvviato = !!t.effettivoInizio;
                                                const isEspletato = getStatoOperativoTurno(t)?.kind === 'espletato';
                                                // Revoca possibile finché il turno non è revocato o espletato; accettato o in
                                                // corso si può ancora revocare.
                                                const canRevoca = !isRevocato && !isEspletato;
                                                // Scadenza modificabile solo se il turno è ancora da accettare.
                                                const canSetScadenza = !isRevocato && !isAvviato && !isRifiutato && !isAccettato;
                                                const hasScadenza = !!t.dataScadenzaAccettazione;
                                                return (
                                                <tr key={t.id} className={`hover:bg-slate-50 transition-colors ${isRevocato || isRifiutato ? 'opacity-50' : ''}`}>
                                                    <td className="p-3 font-medium text-gray-900">
                                                        {new Date(t.dataTurno).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'})}
                                                        {/* Stesso #id citato in email e notifiche ("Turno #id"). */}
                                                        <div className="text-[10px] font-mono text-gray-400 mt-0.5">#{t.id}</div>
                                                    </td>
                                                    <td className="p-3">
                                                        <div className="flex flex-wrap gap-1 items-center">
                                                            <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-xs font-bold border border-blue-100 flex w-max gap-1 items-center"><Clock size={12}/> {t.oraInizio} - {t.oraFine}</span>
                                                            {t.notturno && (
                                                                <span
                                                                    className="bg-emerald-900 text-emerald-100 px-1.5 py-0.5 rounded text-[10px] font-bold"
                                                                    title="Turno notturno: fine il giorno successivo"
                                                                >
                                                                    NOTTURNO
                                                                </span>
                                                            )}
                                                            {/* Badge sui turni con finestra di tolleranza, con il dettaglio nel tooltip. */}
                                                            {(t.rangeInizioDa || t.rangeInizioA || t.rangeFineDa || t.rangeFineA) && (
                                                                <span
                                                                    className="bg-violet-100 text-violet-700 border border-violet-200 px-1.5 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 whitespace-nowrap"
                                                                    title={`Finestra di tolleranza${(t.rangeInizioDa || t.rangeInizioA) ? ` · Inizio ${hhmm(t.rangeInizioDa) || '—'}–${hhmm(t.rangeInizioA) || '—'}` : ''}${(t.rangeFineDa || t.rangeFineA) ? ` · Fine ${hhmm(t.rangeFineDa) || '—'}–${hhmm(t.rangeFineA) || '—'}` : ''}`}
                                                                >
                                                                    <SlidersHorizontal size={11}/> RANGE
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-3 text-gray-700 font-medium">{t.collaboratore?.name || t.collaboratore?.surname || 'Utente rimosso'}</td>
                                                    <td className="p-3 text-center">
                                                        {(() => {
                                                            // Ad avvio avvenuto l'admin vede lo stesso stato operativo del collaboratore
                                                            // (In Servizio / Report / Servizio Espletato); prima vede lo stato di accettazione.
                                                            const op = getStatoOperativoTurno(t);
                                                            if (op) {
                                                                const cls = op.kind === 'espletato' ? 'bg-emerald-600 text-white'
                                                                    : op.kind === 'report' ? 'bg-orange-100 text-orange-700'
                                                                    : op.kind === 'pausa' ? 'bg-amber-100 text-amber-700'
                                                                    : 'bg-emerald-100 text-emerald-700';
                                                                return <span className={`text-[10px] font-bold ${cls} px-2 py-0.5 rounded-full whitespace-nowrap`}>{op.label.toUpperCase()}</span>;
                                                            }
                                                            return isRevocato ? (
                                                                <span className="text-[10px] font-bold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full" title={t.dataRevoca ? new Date(t.dataRevoca).toLocaleString('it-IT') : ''}>REVOCATO</span>
                                                            ) : isRifiutato ? (
                                                                <span className="text-[10px] font-bold bg-rose-50 text-rose-600 px-2 py-0.5 rounded-full" title={t.motivoRifiuto}>RIFIUTATO</span>
                                                            ) : isAccettato ? (
                                                                <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">ACCETTATO</span>
                                                            ) : (
                                                                <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap">DA ACCETTARE</span>
                                                            );
                                                        })()}
                                                        {t.inseritoAPosteriori && (
                                                            <span className="ml-1 text-[10px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full whitespace-nowrap" title="Inserito dal titolare/responsabile dopo lo svolgimento">A POSTERIORI</span>
                                                        )}
                                                    </td>
                                                    <td className="p-3 text-center">
                                                        <div className="inline-flex gap-1">
                                                            {/*
                                                              Disattivazione geofence per singolo turno, visibile solo se il servizio
                                                              ha il geofence attivo.
                                                            */}
                                                            {servizio.geofenceAttivo && (
                                                                <button
                                                                    onClick={() => toggleGeofenceDisabilitato(t.id, !t.geofenceDisabilitato)}
                                                                    className={`${t.geofenceDisabilitato ? 'text-rose-600 bg-rose-50' : 'text-slate-400 hover:text-rose-600'} hover:bg-rose-50 p-1.5 rounded transition-colors`}
                                                                    title={t.geofenceDisabilitato ? 'Geofence DISATTIVO per questo turno (clicca per riattivare)' : 'Disattiva geofence solo per questo turno'}
                                                                >
                                                                    <ShieldOff size={16}/>
                                                                </button>
                                                            )}
                                                            {canSetScadenza && (
                                                                <button
                                                                    onClick={() => openScadenzaModal(t)}
                                                                    className={`${hasScadenza ? 'text-emerald-600 bg-emerald-50' : 'text-slate-400 hover:text-emerald-600'} hover:bg-emerald-50 p-1.5 rounded transition-colors`}
                                                                    title={hasScadenza ? `Scadenza accettazione: ${new Date(t.dataScadenzaAccettazione).toLocaleString('it-IT')}` : 'Imposta scadenza accettazione'}
                                                                >
                                                                    <Clock size={16}/>
                                                                </button>
                                                            )}
                                                            {/* Orari del turno (solo la fine se già avviato); non se revocato o rifiutato. */}
                                                            {!isRevocato && !isRifiutato && (
                                                                <button onClick={() => openEditFineModal(t)}
                                                                        className="text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 p-1.5 rounded transition-colors"
                                                                        title={turnoAvviato(t) ? "Modifica ora di fine (cambia il ricavo cliente)" : "Modifica giorno e orari (cambia il ricavo cliente)"}>
                                                                    <Pencil size={16}/>
                                                                </button>
                                                            )}
                                                            {/* Modifica finestra di tolleranza del turno. */}
                                                            {!isRevocato && !isRifiutato && (
                                                                <button onClick={() => openEditRangeModal(t)}
                                                                        className="text-slate-400 hover:text-violet-600 hover:bg-violet-50 p-1.5 rounded transition-colors"
                                                                        title="Modifica finestra di tolleranza (range) del turno">
                                                                    <SlidersHorizontal size={16}/>
                                                                </button>
                                                            )}
                                                            {canRevoca && (
                                                                <button onClick={() => setConfermaAzione({ tipo: 'revoca', turno: t })}
                                                                        className="text-amber-500 hover:text-amber-700 hover:bg-amber-50 p-1.5 rounded transition-colors"
                                                                        title={isAvviato ? 'Revoca turno già avviato (resta in storia, escluso dall\'agenda)' : 'Revoca turno (resta in storia)'}>
                                                                    <Ban size={16}/>
                                                                </button>
                                                            )}
                                                            <button onClick={() => setConfermaAzione({ tipo: 'elimina', turno: t })}
                                                                    className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded transition-colors"
                                                                    title="Elimina definitivamente dal DB">
                                                                <Trash2 size={16}/>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            )}

            {/*
              Geofence del servizio: se attivo, ogni start/stop fuori dal raggio genera un
              AllertGeofence e un'email a titolare e responsabili, senza bloccare il
              collaboratore. Sta a tutta larghezza sotto la griglia perché con la mappa
              la card diventa alta e sbilancerebbe le colonne.
            */}
            {(isNew || activeTab === 'pianificazione') && (
            // overflow-hidden per sicurezza se react-leaflet produce pane con transform
            // fuori dal box (vedi MapResizer): altrimenti lo scroll di <main> si allunga.
            <div className="relative bg-white p-6 rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between mb-4 border-b pb-2">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <Crosshair size={20} className="text-rose-600"/> Geofence
                    </h3>
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <span className="text-xs font-bold text-gray-500 uppercase">Attivo</span>
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!!servizio.geofenceAttivo}
                            onChange={e => setServizio({...servizio, geofenceAttivo: e.target.checked})}
                        />
                        <span className="relative w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-rose-600 transition-colors before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-5 before:h-5 before:bg-white before:rounded-full before:transition-transform peer-checked:before:translate-x-5"></span>
                    </label>
                </div>
                {servizio.geofenceAttivo ? (
                    <GeofenceMapPicker
                        value={
                            servizio.geofenceLat != null && servizio.geofenceLng != null
                                ? {
                                    lat: Number(servizio.geofenceLat),
                                    lng: Number(servizio.geofenceLng),
                                    raggioMt: Number(servizio.geofenceRaggioMt) || 100,
                                }
                                : null
                        }
                        initialAddress={servizio.indirizzo || undefined}
                        onChange={(v: GeofenceValue) => setServizio({
                            ...servizio,
                            geofenceLat: v.lat,
                            geofenceLng: v.lng,
                            geofenceRaggioMt: v.raggioMt,
                        })}
                    />
                ) : (
                    <p className="text-sm text-gray-500">
                        Quando attivo, lo start/stop di ogni turno fuori dal raggio configurato
                        genera un alert per il titolare (email + sezione Approvazioni). Il
                        collaboratore può comunque procedere col servizio.
                    </p>
                )}
            </div>
            )}

            {!isNew && activeTab === 'report' && servizio.id && (
                <TabReportServizio
                    servizioId={servizio.id}
                    turni={turni}
                    tariffaOrariaCliente={Number(servizio.tariffaOrariaCliente) || 0}
                    tipoTariffaCliente={servizio.tipoTariffaCliente || 'ORARIA'}
                    prezzoForfaitCliente={Number(servizio.prezzoForfaitCliente) || 0}
                    tariffeCollaboratori={tariffe}
                    onTurniRefresh={() => isNew ? undefined : caricaServizio()}
                />
            )}

            {!isNew && activeTab === 'contabilita' && servizio.id && (
                <TabContabilitaServizio
                    servizioId={servizio.id}
                    nomeServizio={servizio.nome}
                    nomeCliente={selectedClient ? getClientLabel(selectedClient) : ''}
                    clientId={selectedClient?.id}
                    tariffaOrariaCliente={Number(servizio.tariffaOrariaCliente) || 0}
                    tipoTariffaCliente={servizio.tipoTariffaCliente || 'ORARIA'}
                    prezzoForfaitCliente={Number(servizio.prezzoForfaitCliente) || 0}
                    tariffeCollaboratori={tariffe}
                    turni={turni}
                    onTurniRefresh={() => { if (!isNew) caricaServizio(); }}
                    datiAggiuntiviJson={(servizio as any).datiAggiuntiviJson || null}
                    onDatiAggiuntiviChange={(json) => setServizio((prev: any) => ({ ...(prev as any), datiAggiuntiviJson: json }))}
                />
            )}

            {/* Modale creazione cliente inline */}
            <ClienteCreateModal
                isOpen={isCreateClientOpen}
                onClose={() => setIsCreateClientOpen(false)}
                onCreated={handleClientCreated}
                initialSearchTerm={clientSearchTerm}
                defaultSedeId={sedeIdForNewClient}
            />

            {avvisoCompresenze.modale}

            <ConflittiDisponibilitaModal
                open={!!conflittiTurni}
                onClose={() => { setConflittiTurni(null); setPendingTurniPayload(null); }}
                collaboratoreNome={conflittiTurni?.collab}
                conflitti={conflittiTurni?.list || []}
                creatiCount={conflittiTurni?.creati}
                totaleRichiesto={conflittiTurni?.totale}
                perRiga
                onConferma={pendingTurniPayload ? handleConfermaOverrideTurni : undefined}
            />

            {/* Modale scadenza accettazione turno */}
            {scadenzaTurno && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !scadenzaSaving && setScadenzaTurno(null)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                            <Clock size={18} className="text-emerald-600"/> Scadenza accettazione
                        </h3>
                        <p className="text-xs text-slate-500 mb-3">
                            Turno del {new Date(scadenzaTurno.dataTurno).toLocaleDateString('it-IT')} · {scadenzaTurno.oraInizio}–{scadenzaTurno.oraFine}
                        </p>
                        <p className="text-[11px] text-slate-500 mb-3">
                            Se il collaboratore non accetta entro la data e ora indicate, il turno viene automaticamente rifiutato. Lasciare vuoto per non impostare una scadenza esplicita (il turno verrà comunque rifiutato in automatico se passa la data del turno senza essere accettato).
                        </p>
                        <input
                            type="datetime-local"
                            value={scadenzaValue}
                            onChange={e => setScadenzaValue(e.target.value)}
                            className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-300 outline-none"
                        />
                        <div className="flex justify-between items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                            <button
                                onClick={() => saveScadenza(true)}
                                disabled={scadenzaSaving || !scadenzaTurno.dataScadenzaAccettazione}
                                className="px-3 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded-lg disabled:opacity-40"
                            >
                                Rimuovi scadenza
                            </button>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setScadenzaTurno(null)}
                                    disabled={scadenzaSaving}
                                    className="px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg"
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={() => saveScadenza(false)}
                                    disabled={scadenzaSaving || !scadenzaValue}
                                    className="px-3 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
                                >
                                    {scadenzaSaving ? <Loader2 size={13} className="animate-spin"/> : <CheckCircle size={13}/>}
                                    Imposta
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale orari turno: giorno/inizio/fine se non avviato, altrimenti solo la fine */}
            {editFineTurno && (() => {
                const soloFine = turnoAvviato(editFineTurno);
                const bloccato = !!editConflitti && (!editConflitti.overridable || !editConferma);
                const notturno = !!editInizioValue && !!editFineValue && editFineValue < (soloFine ? String(editFineTurno.oraInizio || '').slice(0, 5) : editInizioValue);
                return (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !editFineSaving && setEditFineTurno(null)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                            <Pencil size={18} className="text-emerald-600"/> {soloFine ? 'Modifica fine turno' : 'Modifica orari turno'}
                        </h3>
                        {soloFine ? (
                            <p className="text-xs text-slate-500 mb-3">
                                Turno del {new Date(editFineTurno.dataTurno).toLocaleDateString('it-IT')} · inizio {String(editFineTurno.oraInizio || '').slice(0, 5)} <span className="text-slate-400">(turno già avviato: l'inizio non è modificabile)</span>
                            </p>
                        ) : (
                            <p className="text-xs text-slate-500 mb-3">Turno non ancora avviato: puoi spostarlo anche su un altro giorno. Il collaboratore riceve una notifica con il nuovo orario.</p>
                        )}
                        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                            <AlertCircle size={15} className="text-amber-600 shrink-0 mt-0.5"/>
                            <p className="text-[11px] font-bold text-amber-800">
                                Modificando gli orari cambia il RICAVO CLIENTE (ore pianificate × tariffa).
                                {soloFine ? ' Non è possibile ridurre la fine sotto il lavoro già svolto dal collaboratore.' : ''}
                            </p>
                        </div>
                        {!soloFine && (
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div className="col-span-2">
                                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Giorno</label>
                                    <input
                                        type="date"
                                        value={editDataTurno}
                                        onChange={e => cambiaOrarioTurno(setEditDataTurno, e.target.value)}
                                        className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-300 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Inizio</label>
                                    <input
                                        type="time"
                                        value={editInizioValue}
                                        onChange={e => cambiaOrarioTurno(setEditInizioValue, e.target.value)}
                                        className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-300 outline-none"
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Fine</label>
                                    <input
                                        type="time"
                                        value={editFineValue}
                                        onChange={e => cambiaOrarioTurno(setEditFineValue, e.target.value)}
                                        className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-300 outline-none"
                                    />
                                </div>
                            </div>
                        )}
                        {soloFine && (
                            <>
                                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Nuova ora di fine</label>
                                <input
                                    type="time"
                                    value={editFineValue}
                                    onChange={e => setEditFineValue(e.target.value)}
                                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-300 outline-none"
                                    autoFocus
                                />
                            </>
                        )}
                        <p className="text-[11px] text-slate-400 mt-1">
                            {notturno
                                ? 'La fine è precedente all\'inizio: il turno diventa notturno (finisce il giorno dopo).'
                                : 'Se la fine è precedente all\'inizio, il turno diventa notturno (finisce il giorno dopo).'}
                        </p>
                        {editConflitti && (
                            <div className="mt-3">
                                <ConflittiOrario esito={editConflitti} conferma={editConferma} onConferma={setEditConferma}/>
                            </div>
                        )}
                        {editFineError && <p className="text-xs text-red-600 mt-2">{editFineError}</p>}
                        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                            <button
                                onClick={() => setEditFineTurno(null)}
                                disabled={editFineSaving}
                                className="px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={saveEditFine}
                                disabled={editFineSaving || !editFineValue || bloccato}
                                className="px-3 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
                            >
                                {editFineSaving ? <Loader2 size={13} className="animate-spin"/> : <CheckCircle size={13}/>}
                                {soloFine ? 'Salva fine' : editConflitti ? 'Salva comunque' : 'Salva orari'}
                            </button>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* Modale modifica finestra di tolleranza */}
            {editRangeTurno && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !editRangeSaving && setEditRangeTurno(null)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                            <SlidersHorizontal size={18} className="text-violet-600"/> Finestra di tolleranza
                        </h3>
                        <p className="text-xs text-slate-500 mb-3">
                            Turno del {new Date(editRangeTurno.dataTurno).toLocaleDateString('it-IT')} · orario {String(editRangeTurno.oraInizio || '').slice(0, 5)}–{String(editRangeTurno.oraFine || '').slice(0, 5)}
                        </p>
                        <div className="mb-3 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 flex items-start gap-2">
                            <AlertCircle size={15} className="text-violet-600 shrink-0 mt-0.5"/>
                            <p className="text-[11px] font-bold text-violet-800">Se la timbratura del collaboratore cade dentro la finestra, in approvazione l'orario proposto è quello previsto. Ogni campo è opzionale: lascialo vuoto per non porre limiti su quel lato.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1">Inizio · da</label>
                                <input type="time" value={editRange.rangeInizioDa} onChange={e => setEditRange({ ...editRange, rangeInizioDa: e.target.value })} className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-violet-300 outline-none"/>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1">Inizio · a</label>
                                <input type="time" value={editRange.rangeInizioA} onChange={e => setEditRange({ ...editRange, rangeInizioA: e.target.value })} className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-violet-300 outline-none"/>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1">Fine · da</label>
                                <input type="time" value={editRange.rangeFineDa} onChange={e => setEditRange({ ...editRange, rangeFineDa: e.target.value })} className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-violet-300 outline-none"/>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1">Fine · a</label>
                                <input type="time" value={editRange.rangeFineA} onChange={e => setEditRange({ ...editRange, rangeFineA: e.target.value })} className="w-full p-2.5 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-violet-300 outline-none"/>
                            </div>
                        </div>
                        <button
                            onClick={() => setEditRange({ rangeInizioDa: '', rangeInizioA: '', rangeFineDa: '', rangeFineA: '' })}
                            className="mt-2 text-[11px] font-bold text-slate-500 hover:text-rose-600 inline-flex items-center gap-1"
                        >
                            <X size={12}/> Rimuovi finestra (nessun range)
                        </button>
                        {editRangeError && <p className="text-xs text-red-600 mt-2">{editRangeError}</p>}
                        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                            <button onClick={() => setEditRangeTurno(null)} disabled={editRangeSaving} className="px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg">Annulla</button>
                            <button onClick={saveEditRange} disabled={editRangeSaving} className="px-3 py-2 text-xs font-bold bg-violet-600 hover:bg-violet-700 text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5">
                                {editRangeSaving ? <Loader2 size={13} className="animate-spin"/> : <CheckCircle size={13}/>}
                                Salva range
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale conferma revoca / eliminazione turno */}
            {confermaAzione && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
                     onClick={() => !confermaSaving && setConfermaAzione(null)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
                        {(() => {
                            const isRevoca = confermaAzione.tipo === 'revoca';
                            const t = confermaAzione.turno;
                            const isAvviato = !!t.effettivoInizio;
                            return (
                                <>
                                    <h3 className={`text-lg font-bold mb-1 flex items-center gap-2 ${isRevoca ? 'text-amber-700' : 'text-rose-700'}`}>
                                        {isRevoca ? <Ban size={18}/> : <Trash2 size={18}/>}
                                        {isRevoca ? 'Revoca turno?' : 'Elimina turno?'}
                                    </h3>
                                    <p className="text-xs text-slate-500 mb-3">
                                        Turno del {new Date(t.dataTurno).toLocaleDateString('it-IT')} · {t.oraInizio}–{t.oraFine}
                                        {t.collaboratore && <> · {t.collaboratore.name} {t.collaboratore.surname || ''}</>}
                                    </p>
                                    <div className={`text-[12px] leading-relaxed p-3 rounded-lg border ${
                                        isRevoca ? 'bg-amber-50 border-amber-200 text-amber-800'
                                                 : 'bg-rose-50 border-rose-200 text-rose-800'
                                    }`}>
                                        {isRevoca ? (
                                            <>
                                                <p className="font-bold mb-1">Il record resta in storia.</p>
                                                <p>
                                                    Il collaboratore non potrà più aprirlo, il turno viene escluso
                                                    dall'agenda operativa ma rimane per spese / contabilità.
                                                    {isAvviato && ' Il turno è già stato avviato (Start GPS): la revoca lo chiude comunque.'}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="font-bold mb-1">Operazione irreversibile.</p>
                                                <p>
                                                    Il turno viene cancellato definitivamente dal database, insieme a
                                                    eventuali sessioni Start/Stop e foto associate.
                                                    {isAvviato && ' Il turno è già stato avviato: assicurati che le ore/spese non siano da archiviare.'}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                                        <button
                                            onClick={() => setConfermaAzione(null)}
                                            disabled={confermaSaving}
                                            className="px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg"
                                        >
                                            Annulla
                                        </button>
                                        <button
                                            onClick={async () => {
                                                setConfermaSaving(true);
                                                try {
                                                    if (isRevoca) {
                                                        await revocaTurno(t.id);
                                                    } else {
                                                        await eliminaTurno(t.id);
                                                    }
                                                    setConfermaAzione(null);
                                                } finally {
                                                    setConfermaSaving(false);
                                                }
                                            }}
                                            disabled={confermaSaving}
                                            className={`px-3 py-2 text-xs font-bold text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5 ${
                                                isRevoca ? 'bg-amber-600 hover:bg-amber-700' : 'bg-rose-600 hover:bg-rose-700'
                                            }`}
                                        >
                                            {confermaSaving ? <Loader2 size={13} className="animate-spin"/> : (isRevoca ? <Ban size={13}/> : <Trash2 size={13}/>)}
                                            {isRevoca ? 'Revoca' : 'Elimina'}
                                        </button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestioneServizioView;